import { SalesPerformanceRow, StoreSalesPerformanceResponse } from '../../../../../hooks';
import {
  chipDataMapping,
  getAggregatedDataFilterByIndex,
  getIndex,
  StoreSalesPerformanceResponseKey,
} from '../sales.util';

export type SalesPerformanceTableRow = {
  period: StoreSalesPerformanceResponseKey;
  lastYear: number;
  planned: number;
  budget: number;
  vsArea: number;
  vsSalesMarket: number;
  moneyThisYear: number;
  differenceMoney: number;
  vsLastLastWeek: number;
  fullPriceTakt: number;
  fullPriceTaktVsArea: number;
  reducedTakt: number;
  reducedTaktVsArea: number;
};

// Calculate sales performance metrics for a given sales data row
const calculateSalesPerformanceTableData = (sales: SalesPerformanceRow, planned: number, budget: number) => {
  const lastYearIndex = getIndex(sales.salesNet, sales.salesNetLastYear);
  const areaIndex = getIndex(sales.salesNetArea, sales.salesNetAreaLastYear);
  const marketIndex = getIndex(sales.salesNetMarket, sales.salesNetMarketLastYear);

  const fullPrice = {
    fullPriceSales: sales.salesNet - sales.nonFullPriceSales,
    fullPriceSalesLastYear: sales.salesNetLastYear - sales.nonFullPriceSalesLastYear,
    fullPriceSalesArea: sales.salesNetArea - sales.nonFullPriceSalesArea,
    fullPriceSalesAreaLastYear: sales.salesNetAreaLastYear - sales.nonFullPriceSalesLastYearArea,
  };

  const fpTakt = getIndex(fullPrice.fullPriceSales, fullPrice.fullPriceSalesLastYear);
  const reducedTakt = getIndex(sales.nonFullPriceSales, sales.nonFullPriceSalesLastYear);

  return {
    lastYear: lastYearIndex,
    planned: getIndex(sales.salesNet, planned),
    budget: getIndex(sales.salesNet, budget),
    vsArea: getIndex(lastYearIndex, areaIndex),
    vsSalesMarket: getIndex(lastYearIndex, marketIndex),
    moneyThisYear: sales.salesNet,
    differenceMoney: sales.salesNet - sales.salesNetLastYear,
    vsLastLastWeek:
      sales.salesNetLastLastWeek === 0 ? 0 : Math.round((sales.salesNet / sales.salesNetLastLastWeek - 1) * 100),
    fullPriceTakt: fpTakt,
    fullPriceTaktVsArea: getIndex(
      fpTakt,
      getIndex(fullPrice.fullPriceSalesArea, fullPrice.fullPriceSalesAreaLastYear),
    ),
    reducedTakt: reducedTakt,
    reducedTaktVsArea: getIndex(
      reducedTakt,
      getIndex(sales.nonFullPriceSalesArea, sales.nonFullPriceSalesLastYearArea),
    ),
  };
};

// Compute and aggregate sales performance data based on filter criteria
export const aggregateSalesPerformance = (
  response: StoreSalesPerformanceResponse,
  filterIndex?: string,
): Omit<{ [K in StoreSalesPerformanceResponseKey]: SalesPerformanceTableRow }, 'lastCompleteDay'> => {
  const aggregatedData = getAggregatedDataFilterByIndex(response, filterIndex);

  const computedData = {
    currentDay: {
      period: chipDataMapping.LCD,
      ...calculateSalesPerformanceTableData(
        aggregatedData.currentDay,
        response.currentDay.planned,
        response.currentDay.budget,
      ),
    },
    currentWeek: {
      period: chipDataMapping.CW,
      ...calculateSalesPerformanceTableData(
        aggregatedData.currentWeek,
        response.currentWeek.planned,
        response.currentWeek.budget,
      ),
    },
    lastCompleteWeek: {
      period: chipDataMapping.LCW,
      ...calculateSalesPerformanceTableData(
        aggregatedData.lastCompleteWeek,
        response.lastCompleteWeek.planned,
        response.lastCompleteWeek.budget,
      ),
    },
    lastFourWeeks: {
      period: chipDataMapping.L4Wks,
      ...calculateSalesPerformanceTableData(
        aggregatedData.lastFourWeeks,
        response.lastFourWeeks.planned,
        response.lastFourWeeks.budget,
      ),
    },
    accumulatedFinancialYearToDate: {
      period: chipDataMapping.AccTY,
      ...calculateSalesPerformanceTableData(
        aggregatedData.accumulatedFinancialYearToDate,
        response.accumulatedFinancialYearToDate.planned,
        response.accumulatedFinancialYearToDate.budget,
      ),
    },
  };

  return computedData;
};
